import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Banknote, Briefcase, Users, FileText, Send, Calendar, X, Clock } from 'lucide-react';

const PettyCashForm = () => {
  const [formData, setFormData] = useState({ jobOrderId: '', teamId: '', amount: '', reason: '' });
  const [selectedDate, setSelectedDate] = useState('');
  const [jobOrders, setJobOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // ===== FETCH DROPDOWN DATA =====
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const teamRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/team-members`);
        if (Array.isArray(teamRes.data)) setTeams(teamRes.data);
        else if (teamRes.data && Array.isArray(teamRes.data.teams)) setTeams(teamRes.data.teams);
        else setTeams([]);
      } catch { setTeams([]); }

      try {
        const params = selectedDate ? { specificDate: selectedDate } : {};
        let jobRes;
        try {
          jobRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/ops-job-orders`, { params });
        } catch {
          jobRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/active-dropdown`, { params });
        }
        const raw = jobRes.data;
        if (raw && Array.isArray(raw.data)) setJobOrders(raw.data);
        else if (raw && Array.isArray(raw.rows)) setJobOrders(raw.rows);
        else if (Array.isArray(raw)) setJobOrders(raw);
        else setJobOrders([]);
      } catch { setJobOrders([]); }
    };
    fetchDropdownData();
  }, [selectedDate]);

  // ===== FETCH HISTORY =====
  const fetchRequestHistory = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/finance/petty-cash-all`);
      setRecentRequests(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch request history:', error);
    }
  };

  useEffect(() => { fetchRequestHistory(); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/job-orders/petty-cash`, formData);
      if (response.data.success || response.status === 201) {
        setMessage({ type: 'success', text: 'Petty Cash requested successfully!' });
        setFormData({ jobOrderId: '', teamId: '', amount: '', reason: '' });
        setSelectedDate('');
        fetchRequestHistory();
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to submit request.' });
    } finally {
      setLoading(false);
    }
  };

  const getTeamName = (team) => {
    if (!team) return 'Unknown Team';
    if (typeof team === 'string') return team;
    return team.name || team.team_name || team.title || `Team ID: ${team.id}`;
  };

  const getJobName = (jobId) => {
    const j = jobOrders.find(o => (o.id || o.job_id) == jobId);
    return j ? (j.customer_name || j.client_name || `JO-${jobId}`) : `JO-${jobId}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ─── Shared style tokens ───
  const card = {
    background: '#0D1120',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 14,
    overflow: 'hidden',  
  };

  const cardHeader = {
    padding: '18px 20px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const cardBody = { padding: '18px 20px' };

  const label = {
    fontSize: 10,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    marginBottom: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const inputBase = {
    width: '100%',
    height: 40,
    background: '#141927',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 9,
    color: '#E2E8F0',
    fontSize: 13,
    paddingLeft: 36,
    paddingRight: 12,
    outline: 'none',
    fontFamily: "'DM Sans', sans-serif",
  };

  const iconWrap = {
    position: 'absolute',
    left: 11,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
  };

  const iconPill = (color) => ({
    width: 36,
    height: 36,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: color === 'green' ? 'rgba(34,211,165,0.12)' : 'rgba(59,130,246,0.12)',
    border: `1px solid ${color === 'green' ? 'rgba(34,211,165,0.2)' : 'rgba(59,130,246,0.2)'}`,
  });

  const badge = (status) => {
    const map = {
      Approved: { bg: 'rgba(34,211,165,0.1)', color: '#22D3A5', border: 'rgba(34,211,165,0.25)' },
      Rejected: { bg: 'rgba(248,113,113,0.1)', color: '#F87171', border: 'rgba(248,113,113,0.25)' },
    };
    const s = map[status] || { bg: 'rgba(245,158,11,0.1)', color: '#FBBF24', border: 'rgba(245,158,11,0.25)' };
    return {
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: 5,
      fontSize: 9,
      fontWeight: 800,
      letterSpacing: '0.07em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
    };
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: 960, margin: '0 auto', fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Page heading ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
          Finance Operations
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#E2E8F0', marginTop: 2, letterSpacing: '-0.02em' }}>
          Petty Cash Portal
        </div>
      </div>

      {/* ── 2-column grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>

        {/* ─── LEFT: FORM ─── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPill('green')}>
                <Banknote size={16} color="#22D3A5" />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
                  New Request
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#E2E8F0', marginTop: 1 }}>
                  Submit Petty Cash
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              <span style={{ color: '#22D3A5', fontWeight: 700 }}>● </span>Live
            </div>
          </div>

          <div style={cardBody}>
            {message.text && (
              <div style={{
                padding: '9px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 14,
                background: message.type === 'success' ? 'rgba(34,211,165,0.1)' : 'rgba(239,68,68,0.1)',
                color: message.type === 'success' ? '#22D3A5' : '#EF4444',
                border: `1px solid ${message.type === 'success' ? 'rgba(34,211,165,0.25)' : 'rgba(239,68,68,0.25)'}`,
              }}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* Row 1: Date + Job Order */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>

                {/* Date filter */}
                <div>
                  <div style={label}>
                    Date filter
                    {selectedDate && (
                      <span
                        onClick={() => { setSelectedDate(''); setFormData({ ...formData, jobOrderId: '' }); }}
                        style={{ fontSize: 10, color: '#EF4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <X size={11} /> clear
                      </span>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Calendar size={14} color="rgba(255,255,255,0.3)" /></span>
                    <input
                      type="date"
                      style={{ ...inputBase, colorScheme: 'dark' }}
                      value={selectedDate}
                      onChange={(e) => { setSelectedDate(e.target.value); setFormData({ ...formData, jobOrderId: '' }); }}
                    />
                  </div>
                </div>

                {/* Job Order */}
                <div>
                  <div style={label}>
                    Job order{' '}
                    <span style={{ color: '#3B82F6', fontWeight: 600 }}>
                      ({Array.isArray(jobOrders) ? jobOrders.length : 0})
                    </span>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Briefcase size={14} color="rgba(255,255,255,0.3)" /></span>
                    <select
                      name="jobOrderId"
                      style={{ ...inputBase, cursor: 'pointer', WebkitAppearance: 'none' }}
                      value={formData.jobOrderId}
                      onChange={handleChange}
                      required
                    >
                      <option value="" style={{ background: '#1E2535' }}>
                        {!jobOrders.length ? 'No jobs...' : 'Select job...'}
                      </option>
                      {jobOrders.map(o => {
                        const id = o.id || o.job_id;
                        return (
                          <option key={id} value={id} style={{ background: '#1E2535' }}>
                            {o.customer_name || o.client_name || `JO-${id}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0 14px' }} />

              {/* Row 2: Team + Amount */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>

                {/* Team */}
                <div>
                  <div style={label}>Assigned team</div>
                  <div style={{ position: 'relative' }}>
                    <span style={iconWrap}><Users size={14} color="rgba(255,255,255,0.3)" /></span>
                    <select
                      name="teamId"
                      style={{ ...inputBase, cursor: 'pointer', WebkitAppearance: 'none' }}
                      value={formData.teamId}
                      onChange={handleChange}
                      required
                    >
                      <option value="" style={{ background: '#1E2535' }}>Select team...</option>
                      {teams.map((t, i) => {
                        const name = getTeamName(t);
                        return <option key={i} value={name} style={{ background: '#1E2535' }}>{name}</option>;
                      })}
                    </select>
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <div style={label}>Amount (PKR)</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ ...iconWrap, color: '#F59E0B', fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>
                      ₨
                    </span>
                    <input
                      type="number"
                      name="amount"
                      placeholder="0.00"
                      style={{ ...inputBase, fontFamily: "'DM Mono', monospace", fontSize: 14 }}
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div style={{ marginBottom: 20 }}>
                <div style={label}>
                  Reason / details
                  <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9 }}>optional</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ ...iconWrap, top: 13, transform: 'none' }}>
                    <FileText size={14} color="rgba(255,255,255,0.3)" />
                  </span>
                  <textarea
                    name="reason"
                    placeholder="Fuel, transport, site materials..."
                    style={{
                      ...inputBase,
                      height: 72,
                      paddingTop: 10,
                      resize: 'vertical',
                      lineHeight: 1.5,
                    }}
                    value={formData.reason}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', height: 42, borderRadius: 10,
                  background: loading ? 'rgba(59,130,246,0.5)' : '#3B82F6',
                  border: 'none', color: '#fff', fontWeight: 700, fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading ? 'Submitting...' : <><Send size={14} /> Submit Request</>}
              </button>
            </form>
          </div>
        </div>

        {/* ─── RIGHT: HISTORY ─── */}
        <div style={card}>
          <div style={cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={iconPill('blue')}>
                <Clock size={16} color="#60A5FA" />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
                  History
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#E2E8F0', marginTop: 1 }}>
                  My Requests
                </div>
              </div>
            </div>
            <div style={{
              background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#60A5FA',
            }}>
              {recentRequests.length} total
            </div>
          </div>

          <div style={{ ...cardBody, paddingTop: 4, maxHeight: 460, overflowY: 'auto' }}>
            {recentRequests.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '32px 0',
                color: 'rgba(255,255,255,0.3)', fontSize: 12,
                border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 10, marginTop: 8,
              }}>
                No requests found.
              </div>
            ) : (
              recentRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: '13px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 10,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0', marginBottom: 3 }}>
                      {getJobName(req.job_order_id)}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                      {req.team_id} · {formatTime(req.requested_at)}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
                      {req.reason ? `"${req.reason}"` : 'No details provided.'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 16, fontWeight: 800, color: '#F59E0B',
                      fontFamily: "'DM Mono', monospace", marginBottom: 5,
                    }}>
                      ₨{req.amount}
                    </div>
                    <span style={badge(req.status)}>
                      {req.status ? req.status.toUpperCase() : 'PENDING'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PettyCashForm;